/* -----------------Helpers & Hooks--------------- */
import { useDevice } from "@memorang/ui/useDevice";
import { useRouter } from "expo-router";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useInvalidateQueries from "@hooks/useInvalidateQueries";
/* -----------------Types--------------- */
import type { ExamDetails } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { PurchaseSuccessSkeleton } from "@memorang/ui/components/PurchaseSuccessApplet/PurchaseSuccessSkeleton";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { fetchExamDetails } from "api/commerce/exam-service";
import type { ApplyAccessCodeResponse } from "api/commerce/types";
/* -----------------Child components--------------- */
import RedeemCodeSuccessView from "./RedeemCodeSuccessView";

// Mirrors app/(protected)/purchase/[id]/success.tsx's getDescription().
const getSingleProductDescription = (bundleType: string, t: TFunction) => {
	if (bundleType === "STUDY_PACK") {
		return t("purchase.success.studyPack");
	}
	if (bundleType === "PRACTICE_TEST") {
		return t("purchase.success.practiceTest");
	}
	if (bundleType === "EXAM") {
		return t("purchase.success.combo");
	}
};

const getProductsMapping = (
	accessCodeResult: ApplyAccessCodeResponse,
	t: TFunction,
) => {
	return accessCodeResult.productData && accessCodeResult.productData.length > 1
		? accessCodeResult.productData.map((product) => {
				return {
					icon: product.icon,
					title: product.bundleName,
					id: product.bundleId,
					description:
						product.bundleType === "PRACTICE_TEST"
							? t("purchase.success.practiceTest")
							: t("purchase.success.studyPack"),
					features: product.features,
					isCustomAccess: product.isCustomAccess,
					examId: product.examId,
					bundleType: product.bundleType,
					beginStartDate: product.beginStartDate,
					endStartDate: product.endStartDate,
				};
			})
		: [
				{
					icon: accessCodeResult.data.icon,
					title: accessCodeResult.data.bundleName,
					id: accessCodeResult.data.bundleId,
					description: getSingleProductDescription(
						accessCodeResult.data.bundleType,
						t,
					),
					features: accessCodeResult.data.features,
					isCustomAccess: accessCodeResult.data.isCustomAccess,
					examId: accessCodeResult.data.examId,
					bundleType: accessCodeResult.data.bundleType,
					beginStartDate: accessCodeResult.data.beginStartDate,
					endStartDate: accessCodeResult.data.endStartDate,
				},
			];
};

const RedeemCodeSuccessContainer = ({
	accessCodeResult,
}: { accessCodeResult: ApplyAccessCodeResponse }) => {
	const { isMobile } = useDevice();
	const { t } = useTranslation();

	const router = useRouter();
	const [examDetails, setExamDetails] = useState<ExamDetails | null>(null);

	const [showFreeQuizDialog, setShowFreeQuizDialog] = useState(false);

	const examId =
		typeof accessCodeResult.data.examId === "string"
			? accessCodeResult.data.examId
			: accessCodeResult.productData?.[0]?.examId;

	const { invalidateDistributionsAndMetadata } = useInvalidateQueries({
		productId: accessCodeResult.data.bundleId,
	});

	const handleNavigateToProduct = () => {
		if (examDetails) {
			const hasPracticeTests = accessCodeResult.productData?.find(
				(item) => item.bundleType === "PRACTICE_TEST",
			);
			const url =
				!hasPracticeTests || isMobile ? "/study-pack" : "/practice-tests";
			router.push(url);
		}
	};
	const handleCloseFreeQuizDialog = (navigate?: boolean) => {
		setShowFreeQuizDialog(false);
		if (navigate) {
			handleNavigateToProduct();
		}
	};

	useEffect(() => {
		if (examId) {
			invalidateDistributionsAndMetadata();
			fetchExamDetails({
				examId: examId!,
			}).then((response) => setExamDetails(response));
		}
	}, [examId, invalidateDistributionsAndMetadata]);

	if (!(accessCodeResult && examDetails)) {
		return (
			<ScrollContainer maxWidth="xs">
				<Box paddingVertical={16} paddingHorizontal={16}>
					<PurchaseSuccessSkeleton
						products={getProductsMapping(accessCodeResult, t).map(
							(product) => ({
								numFeatures: product.isCustomAccess
									? 0
									: (product.features?.length ?? 0),
							}),
						)}
						titleLines={1}
						summaryLines={isMobile ? 5 : 4}
						numActions={1}
					/>
				</Box>
			</ScrollContainer>
		);
	}

	const title = t("redeemCode.titleGranted");
	const products = getProductsMapping(accessCodeResult, t);

	return (
		<RedeemCodeSuccessView
			examName={examDetails?.exam.name || ""}
			products={products}
			title={title}
			showFreeQuizDialog={showFreeQuizDialog}
			handleCloseFreeQuizDialog={handleCloseFreeQuizDialog}
			examDetails={examDetails}
		/>
	);
};

export default RedeemCodeSuccessContainer;
